import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downloadGet, get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, PageSkeleton, Input } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { AboutTab } from './AboutTab';
import { NumberingTab } from './NumberingTab';
import { IntegrationsTab } from './IntegrationsTab';
import { EmailSettingsTab } from './EmailSettingsTab';
import { LetterheadCard } from './LetterheadCard';
import { ImageLetterheadCard } from './ImageLetterheadCard';
import { ADD_ONS, MODULE_CATALOGUE, normaliseUiLocale, resolveOpdSettings, type LetterheadSettings, type ModuleCode } from '@his/shared';
import { OpdClinicSettingsCard } from '../opd/OpdClinicSettingsCard';
import { EmrFormsTab } from './EmrFormsTab';
import { emrFormsTabVisible } from './emr-forms-draft';
import { StaffAlertSmsCard } from './StaffAlertSmsCard';
import { IpdSchedulesCard } from './IpdSchedulesCard';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'clinical', label: 'Clinical' },
  { id: 'emr_forms', label: 'EMR forms' },
  { id: 'numbering', label: 'Numbering' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'email', label: 'Email' },
  { id: 'about', label: 'About & updates' },
] as const;
type TabId = (typeof TABS)[number]['id'];

function isTabId(value: string | null): value is TabId {
  return TABS.some((tab) => tab.id === value);
}

interface SettingsResponse {
  settings: Record<string, unknown>;
  tenant: {
    id: string; name: string; slug: string; status: string;
    timezone: string; locale: string; currency: string;
    logoUrl: string | null; primaryColor: string | null; modules: string[];
  } | null;
}

interface RetiredModule {
  module: string;
  label: string;
  tables: { table: string; label: string }[];
}

const moduleName = (code: string) => MODULE_CATALOGUE.find((module) => module.code === code)?.name ?? code;

/** Tenant configuration (SRS 3.4). */
export function SettingsPage() {
  const { can, hasModule, canWrite, isSuperAdmin, user, refreshUser } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<string, string | number | boolean>>({});
  // `?tab=about` is a deep link: the update banner, the landing page's "Check
  // my version" and docs all point straight at the About tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab: TabId = isTabId(requestedTab) ? requestedTab : 'general';
  const selectTab = (id: string) => setSearchParams(id === 'general' ? {} : { tab: id }, { replace: true });

  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => get<SettingsResponse>('/org/settings'),
  });

  const retired = useQuery({
    queryKey: ['retired-modules'],
    queryFn: () => get<RetiredModule[]>('/org/modules/retired'),
    enabled: Boolean(user?.retiredModules?.length),
  });

  useEffect(() => {
    if (!settings.data) return;
    const s = settings.data.settings as Record<string, unknown>;
    const opd = resolveOpdSettings(s);
    setForm({
      displayName: (s.displayName as string) ?? settings.data.tenant?.name ?? '',
      locale: normaliseUiLocale(settings.data.tenant?.locale),
      primaryColor: settings.data.tenant?.primaryColor ?? '#0f766e',
      reminderHours: Array.isArray(s.reminderHours) ? (s.reminderHours as number[]).join(', ') : '24, 2',
      sessionTimeoutMinutes: (s.sessionTimeoutMinutes as number) ?? 60,
      enforce2faForAdmins: (s.enforce2faForAdmins as boolean) ?? true,
      requireCosignForMedicalOfficer: opd.requireCosignForMedicalOfficer,
      wardStockModel: (s.wardStockModel as string) ?? 'unit_dose',
    });
  }, [settings.data]);

  const hasOpd = hasModule('opd');
  const hasCosign = hasOpd || hasModule('ipd') || hasModule('er');
  const hasIpd = hasModule('ipd');

  const saveGeneral = useMutation({
    mutationFn: () =>
      put('/org/settings', {
        displayName: String(form.displayName),
        locale: String(form.locale),
        primaryColor: String(form.primaryColor),
        sessionTimeoutMinutes: Number(form.sessionTimeoutMinutes),
        enforce2faForAdmins: Boolean(form.enforce2faForAdmins),
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'settings_saved'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      void refreshUser();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_settings'), describeError(error)),
  });

  const saveClinical = useMutation({
    mutationFn: () =>
      put('/org/settings', {
        ...(hasOpd
          ? {
              reminderHours: String(form.reminderHours ?? '24, 2')
                .split(/[,\s]+/)
                .filter(Boolean)
                .map(Number),
            }
          : {}),
        ...(hasCosign ? { requireCosignForMedicalOfficer: Boolean(form.requireCosignForMedicalOfficer) } : {}),
        ...(hasIpd ? { wardStockModel: String(form.wardStockModel) as 'unit_dose' | 'ward_stock' } : {}),
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'clinical_settings_saved'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_settings'), describeError(error)),
  });

  if (settings.isLoading) {
    return (
      <PageSkeleton />
    );
  }

  const readOnly = !can('tenant.settings.manage') || !canWrite;
  const heldAddOns = (Object.keys(ADD_ONS) as ModuleCode[]).filter((code) => hasModule(code));
  const storedSettings = (settings.data?.settings as Record<string, unknown> | undefined) ?? {};

  const visibleTabs = TABS.filter((t) => {
    if (t.id === 'clinical') return hasCosign;
    if (t.id === 'integrations') return heldAddOns.length > 0 && can('integration.read', 'integration.manage', 'nphies.settings');
    if (t.id === 'email') return can('tenant.settings.read', 'tenant.settings.manage');
    if (t.id === 'emr_forms') return emrFormsTabVisible(hasModule, can);
    return true;
  });

  return (
    <>
      <PageHeader
        title={tr('admin', 'settings')}
        description={settings.data?.tenant ? `${settings.data.tenant.name} · ${settings.data.tenant.slug}` : undefined}
        actions={
          !readOnly &&
          ((tab === 'general' && <Button onClick={() => saveGeneral.mutate()} loading={saveGeneral.isPending}>{tr('admin', 'save_changes')}</Button>) ||
            (tab === 'clinical' && <Button onClick={() => saveClinical.mutate()} loading={saveClinical.isPending}>{tr('admin', 'save_changes')}</Button>))
        }
      />

      <Tabs
        tabs={visibleTabs.map((t) =>
          t.id === 'email' ? { ...t, label: tr('admin', 'email') } : t.id === 'emr_forms' ? { ...t, label: tr('admin', 'emr_forms') } : t,
        )}
        active={tab}
        onChange={selectTab}
      />

      {!canWrite && can('tenant.settings.manage') && <Alert className="mb-4">{tr('admin', 'changes_are_made_on_your_facilitys_own_server')}</Alert>}

      {tab === 'about' && (
        <AboutTab
          canManage={!readOnly}
          tenant={settings.data?.tenant ? { name: settings.data.tenant.name, slug: settings.data.tenant.slug } : null}
        />
      )}

      {tab === 'integrations' && heldAddOns.length > 0 && <IntegrationsTab />}

      {tab === 'email' && <EmailSettingsTab readOnly={readOnly} />}

      {tab === 'emr_forms' && emrFormsTabVisible(hasModule, can) && <EmrFormsTab />}

      {tab === 'numbering' && (
        <NumberingTab
          readOnly={readOnly}
          patterns={(settings.data?.settings.numberPatterns as Record<string, string> | undefined) ?? {}}
          timezone={settings.data?.tenant?.timezone}
          modules={settings.data?.tenant?.modules ?? []}
          retiredModules={user?.retiredModules ?? []}
        />
      )}

      {tab === 'clinical' && hasCosign && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('admin', 'clinical_policy')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {hasOpd && (
              <Field label={tr('admin', 'appointment_reminders_hours_before')} hint={tr('admin', 'one_to_four_whole_hours_e_g_24_2_each_patient')}>
                <Input
                  value={String(form.reminderHours ?? '24, 2')}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, reminderHours: e.target.value })}
                />
              </Field>
            )}
            {/* How medicine leaves the pharmacy for a ward. A facility with no
                wards has no such decision to make, and offering it only invited
                someone to set a policy that governs nothing. */}
            {hasIpd && (
              <Field label={tr('admin', 'ward_stock_model')} hint={tr('admin', 'unit_dose_bills_each_issue_to_the_patient')} className="sm:col-span-2">
                <ChoiceButtons
                  value={String(form.wardStockModel ?? 'unit_dose')}
                  disabled={readOnly}
                  onChange={(wardStockModel) => setForm({ ...form, wardStockModel })}
                  options={[
                    { value: 'unit_dose', label: tr('admin', 'unit_dose_billed_per_issue') },
                    { value: 'ward_stock', label: tr('admin', 'ward_stock_issued_to_the_ward') },
                  ]}
                />
              </Field>
            )}
            <Checkbox
              className="sm:col-span-2"
              checked={Boolean(form.requireCosignForMedicalOfficer)}
              disabled={readOnly}
              onCheckedChange={(checked) => setForm({ ...form, requireCosignForMedicalOfficer: checked })}
              label={tr('admin', 'medical_officers_orders_require_a_consultant_co')}
            />
          </CardContent>
        </Card>
      )}

      {tab === 'clinical' && hasOpd && <OpdClinicSettingsCard readOnly={readOnly} />}

      {tab === 'clinical' && hasIpd && <IpdSchedulesCard readOnly={readOnly} />}

      {tab === 'clinical' && hasIpd && <StaffAlertSmsCard readOnly={readOnly} />}

      {tab === 'general' && readOnly && canWrite && <Alert className="mb-4">{tr('admin', 'you_can_view_these_settings_but_not_change_them')}</Alert>}

      {tab === 'general' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{tr('admin', 'identity')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label={tr('admin', 'display_name')} className="sm:col-span-2">
                <Input
                  value={String(form.displayName ?? '')}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                />
              </Field>
              <Field label={tr('admin', 'primary_colour')} hint={tr('admin', 'used_on_letterheads_and_the_app')}>
                <Input
                  type="color"
                  className="h-9 w-full p-1"
                  value={String(form.primaryColor ?? '#0f766e')}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                />
              </Field>
              <Field label={tr('admin', 'language')}>
                <ChoiceButtons
                  value={String(form.locale ?? 'en')}
                  disabled={readOnly}
                  onChange={(locale) => setForm({ ...form, locale })}
                  options={[
                    { value: 'en', label: tr('admin', 'english') },
                    { value: 'ur-Latn', label: tr('admin', 'roman_urdu') },
                  ]}
                />
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{tr('admin', 'staff_sign_in_policy')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <Field label={tr('admin', 'session_timeout_minutes')}>
                <Input
                  type="number"
                  min={5}
                  max={1440}
                  value={Number(form.sessionTimeoutMinutes ?? 60)}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, sessionTimeoutMinutes: Number(e.target.value) })}
                />
              </Field>
              <Checkbox
                checked={Boolean(form.enforce2faForAdmins)}
                disabled={readOnly}
                onCheckedChange={(checked) => setForm({ ...form, enforce2faForAdmins: checked })}
                label={tr('admin', 'require_two_factor_sign_in_for_counter_and_back')}
                description={tr('admin', 'cashier_accountant_hr_officer_and_auditor_super')}
              />
              <Link to="/account/security" className="inline-flex w-fit items-center gap-1.5 rounded-md text-xs font-medium text-primary underline-offset-4 hover:underline focus-ring">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                {tr('admin', 'own_two_factor_is_in_account_security')}
              </Link>
            </CardContent>
          </Card>

          <LetterheadCard
            readOnly={readOnly}
            stored={storedSettings.letterhead as LetterheadSettings | undefined}
            logoUrl={settings.data?.tenant?.logoUrl ?? null}
          />

          <ImageLetterheadCard readOnly={readOnly} />

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm">{tr('admin', 'licensed_modules')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {tr('admin', 'modules_are_licensed_by_the_platform_owner')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {settings.data?.tenant?.modules.map((module) => (
                  <Badge key={module} variant="secondary">
                    {moduleName(module)}
                  </Badge>
                ))}
              </div>
              {(retired.data?.length ?? 0) > 0 && (
                <div className="space-y-2 border-t pt-3">
                  <p className="text-sm font-medium">{tr('admin', 'archived_no_longer_in_your_plan_still_readable')}</p>
                  {retired.data?.map((entry) => (
                    <div key={entry.module} className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge variant="muted">{entry.label}</Badge>
                      {isSuperAdmin &&
                        entry.tables.map((table) => (
                          <Button
                            key={table.table}
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              void downloadGet(`/org/modules/${entry.module}/export/${table.table}.csv`, `${entry.module}-${table.table}.csv`).catch(
                                (error: unknown) => toast.error(tr('admin', 'could_not_export'), describeError(error)),
                              )
                            }
                          >
                            {tr('admin', 'raw_data_export_for_your_it_or_auditor')} {table.label}
                          </Button>
                        ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
